import React, { useEffect, useState, useRef } from "react";
import axios from "axios";

import {
  getFolderContent, API, downloadFiles, compressAllImages, getUsage,
  getMe, setToken, clearFolderKeys,
} from "./api";
import { formatSize, usageText } from "./media";
import ExplorerView from "./components/ExplorerView";
import ActionBar from "./components/ActionBar";
import Sidebar from "./components/Sidebar";
import Breadcrumb from "./components/Breadcrumb";
import ConfirmDialog from "./components/ConfirmDialog";
import ConfirmPasswordDialog from "./components/ConfirmPasswordDialog";
import UploadModal from "./components/UploadModal";
import UploadProgress from "./components/UploadProgress";
import NotifyCenter from "./components/NotifyCenter";
import TrashModal from "./components/TrashModal";
import { LockedFolderPanel, LockDialog, PasswordInput, MIN_PASSWORD_LENGTH } from "./components/FolderLock";
import LoginPopup from "./LoginPopup";
import namingRules from "./namingRules";
import {
  IcoArrowLeft, IcoAlert, IcoFolderPlus, IcoDownload, IcoInfo, IcoX, IcoCompress, IcoTrash, IcoLock,
} from "./components/Icons";

import "./App.css";

const allowedExt = [
  "jpg", "jpeg", "png", "gif", "webp",
  "mp4", "mov", "avi", "mkv", "webm",
  "m4v", "wmv", "flv", "3gp",
  "pdf",
];

const systemFiles = ["thumbs.db", ".ds_store", "desktop.ini"];

const normalize = (str) =>
  str
    ?.normalize("NFD")
    .replace(/[\u0300-\u036f]/g, "")
    .toLowerCase()
    .replace(/\s+/g, "-");

const sleep = (ms) => new Promise((resolve) => setTimeout(resolve, ms));

const byName = (a, b) => a.localeCompare(b, "vi", { numeric: true, sensitivity: "base" });

// Backend chay sau IIS/ARR reverse proxy - thinh thoang bi timeout/502 khi
// service phia sau restart. Retry + timeout de tu phuc hoi thay vi phai F5.
const FOLDER_LOAD_TIMEOUT_MS = 15000;
const FOLDER_LOAD_MAX_RETRIES = 2;
const FOLDER_LOAD_RETRY_DELAY_MS = 1000;

const TOAST_MS = 3200;

const isRetryableFolderError = (err) => {
  if (err?.name === "AbortError") return true; // timeout
  if (typeof err?.status === "number") return err.status >= 500; // loi server
  return true; // loi mang (fetch throw truoc khi co response)
};

export default function App() {
  const [role, setRole] = useState(localStorage.getItem("role"));
  const [userName, setUserName] = useState(localStorage.getItem("userName") || "");
  const [username, setUsername] = useState(localStorage.getItem("username") || "");
  const [showLogin, setShowLogin] = useState(!role);

  const [deleteFolderConfirm, setDeleteFolderConfirm] = useState(null);

  const [path, setPath] = useState("");
  const [folders, setFolders] = useState([]);
  const [images, setImages] = useState([]);
  const [folderLoading, setFolderLoading] = useState(false);
  const [folderError, setFolderError] = useState(null);
  const loadSeqRef = useRef(0);

  // Thư mục mà nội dung đang hiển thị thuộc về (null = chưa tải lần nào).
  // Khác `path` nghĩa là đang chuyển thư mục → hiện khung chờ thay cho nội dung cũ.
  const [loadedPath, setLoadedPath] = useState(null);
  // Các thư mục gốc, dùng cho lối tắt ở thanh bên.
  const [rootFolders, setRootFolders] = useState([]);
  const [navOpen, setNavOpen] = useState(false);

  const [showCreateModal, setShowCreateModal] = useState(false);
  const [showUploadImageModal, setShowUploadImageModal] = useState(false);
  const [showUploadFolderModal, setShowUploadFolderModal] = useState(false);

  const [search, setSearch] = useState("");
  const [newFolderName, setNewFolderName] = useState("");
  const [newFolderLocked, setNewFolderLocked] = useState(false);
  const [newFolderPassword, setNewFolderPassword] = useState("");

  // Mật khẩu thư mục (máy chủ trả kèm danh sách thư mục):
  //  lockedFolders: thư mục con có mật khẩu; manageableFolders: thư mục con mà người
  //  đang đăng nhập được đặt/đổi/gỡ mật khẩu; folderLock: tình trạng của thư mục đang mở.
  const [lockedFolders, setLockedFolders] = useState([]);
  const [manageableFolders, setManageableFolders] = useState([]);
  const [folderLock, setFolderLock] = useState(null);
  const [rootLocked, setRootLocked] = useState([]);
  // Thư mục đang mở bị chặn vì có mật khẩu: { path, lockPath, ownerName }
  const [lockedInfo, setLockedInfo] = useState(null);
  // Thư mục đang đặt/đổi mật khẩu: { path, name, locked }
  const [lockTarget, setLockTarget] = useState(null);

  const [confirmData, setConfirmData] = useState(null);

  // Ảnh/thư mục nào đang được trang web khác tải (để cảnh báo trước khi xóa).
  const [usage, setUsage] = useState({ files: {}, folders: {} });
  const [showTrash, setShowTrash] = useState(false);

  const [notifyCenter, setNotifyCenter] = useState("");
  const [notifyType, setNotifyType] = useState("success");
  const notifyTimerRef = useRef(null);

  const showPopup = (msg, type = "success") => {
    setNotifyCenter(msg);
    setNotifyType(type);
    // Thông báo mới thay thông báo cũ: hủy hẹn giờ cũ để nó không tắt nhầm cái mới.
    clearTimeout(notifyTimerRef.current);
    notifyTimerRef.current = setTimeout(() => setNotifyCenter(""), TOAST_MS);
  };

  // Upload state
  const [uploading, setUploading] = useState(false);
  const [progress, setProgress] = useState(0);
  const [speed, setSpeed] = useState(0);
  const [eta, setEta] = useState(0);

  const lastLoadedRef = useRef(0);
  const lastTimeRef = useRef(Date.now());

  const [showDownloadModal, setShowDownloadModal] = useState(false);
  const [selectedDownload, setSelectedDownload] = useState([]);
  const [downloading, setDownloading] = useState(false);

  const [compressing, setCompressing] = useState(false);

  const compressNote = "Ảnh JPG/PNG trên 5 MB sẽ được tự động nén khi tải lên.";

  const getUploadNote = () => {
    if (!path) return `Chỉ tải lên file hợp lệ.\n${compressNote}`;

    const rootFolder = path.split("/")[0];
    const key = normalize(rootFolder);
    const rules = namingRules[key];

    if (!rules) return `Chỉ tải lên file hợp lệ.\n${compressNote}`;

    return `- Đặt file theo tên: ${rules.prefix}_${rules.nameLabel}.png
- Ví dụ: ${rules.prefix}_${rules.example}.png
- ${compressNote}`;
  };

  // Load folder
  useEffect(() => {
    if (!showLogin) {
      loadFolder(path);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [path, showLogin]);

  // Mở trang khi đã đăng nhập từ trước: hỏi máy chủ xem vé đăng nhập còn dùng được
  // không. Hết hạn (hoặc đăng nhập từ trước khi có vé) thì mời đăng nhập lại, vì thiếu
  // vé máy chủ không nhận ra người tạo thư mục.
  useEffect(() => {
    if (showLogin) return;
    let cancelled = false;
    getMe().then((me) => {
      if (cancelled) return;
      if (me.expired) { logout(); return; }
      // Vai trò do máy chủ quyết định theo mã số (chỉ mã số quản trị mới là "admin").
      // Người đăng nhập từ trước có thể còn lưu vai trò cũ trong trình duyệt -> sửa lại.
      if (me.ok && (me.role === "admin" || me.role === "user") && me.role !== role) {
        localStorage.setItem("role", me.role);
        setRole(me.role);
      }
    });
    return () => { cancelled = true; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [showLogin]);

  const loadFolder = async (p) => {
    const seq = ++loadSeqRef.current;
    setFolderLoading(true);
    setFolderError(null);
    setLockedInfo(null);

    for (let attempt = 0; attempt <= FOLDER_LOAD_MAX_RETRIES; attempt++) {
      if (seq !== loadSeqRef.current) return; // co request moi hon, bo ket qua cu

      const controller = new AbortController();
      const timer = setTimeout(() => controller.abort(), FOLDER_LOAD_TIMEOUT_MS);

      try {
        const data = await getFolderContent(p, { signal: controller.signal });
        if (seq !== loadSeqRef.current) return;
        setFolders(data.folders || []);
        setImages(data.media || []);
        setLockedFolders(data.lockedFolders || []);
        setManageableFolders(data.manageableFolders || []);
        setFolderLock(data.lock || null);
        setLoadedPath(p);
        if (p === "") {
          setRootFolders([...(data.folders || [])].sort(byName));
          setRootLocked(data.lockedFolders || []);
        }
        setFolderLoading(false);
        setUsage({ files: {}, folders: {} });
        getUsage(p, data.folders || []).then((u) => {
          if (seq === loadSeqRef.current) setUsage(u);
        });
        return;
      } catch (err) {
        if (seq !== loadSeqRef.current) return;

        if (err?.locked) {
          // Thư mục có mật khẩu: hiện ô nhập mật khẩu thay cho nội dung.
          setFolderLoading(false);
          setLockedInfo({ path: p, ...err.locked });
          return;
        }

        const canRetry =
          attempt < FOLDER_LOAD_MAX_RETRIES && isRetryableFolderError(err);
        if (canRetry) {
          await sleep(FOLDER_LOAD_RETRY_DELAY_MS * (attempt + 1));
          continue;
        }

        setFolderLoading(false);
        setFolderError(
          err?.name === "AbortError"
            ? "Kết nối đến máy chủ quá lâu, có thể máy chủ đang khởi động lại."
            : "Không kết nối được với máy chủ."
        );
        showPopup("Không mở được thư mục, vui lòng thử lại!", "error");
        return;
      } finally {
        clearTimeout(timer);
      }
    }
  };

  // Chan khong cho dieu huong chong len nhau khi dang tai (vd SMB cham/loi
  // mang) - neu khong, breadcrumb se cu don don them ma noi dung khong doi.
  // Đổi thư mục thì xóa ô lọc: từ khóa của thư mục cũ không còn ý nghĩa.
  const goIntoFolder = (folder) => {
    if (folderLoading) return;
    setSearch("");
    setPath(path ? `${path}/${folder}` : folder);
  };

  const goBack = () => {
    if (folderLoading || !path) return;
    const parts = path.split("/");
    parts.pop();
    setSearch("");
    setPath(parts.join("/") || "");
  };

  const goToPath = (p) => {
    if (folderLoading) return;
    setSearch("");
    setPath(p);
  };

  const validateFiles = (files) => {
    for (let f of files) {
      if (systemFiles.includes(f.name.toLowerCase())) continue;
      const ext = f.name.split(".").pop().toLowerCase();
      if (!allowedExt.includes(ext)) {
        showPopup(`Không hỗ trợ: ${f.name}`, "error");
        return false;
      }
    }
    return true;
  };

  // ================== CREATE FOLDER ==================
  const createFolder = async () => {
    if (!newFolderName.trim()) return;

    const withPassword = newFolderLocked && newFolderPassword.length >= MIN_PASSWORD_LENGTH;
    if (newFolderLocked && !withPassword) return;

    try {
      await axios.post(`${API}/folder/create`, {
        folderName: newFolderName,
        parent: path,
        ownerName: userName,
        ...(withPassword ? { password: newFolderPassword } : {}),
      });
      loadFolder(path);
      showPopup(withPassword ? "Đã tạo thư mục có mật khẩu" : "Tạo thư mục thành công!");
    } catch (err) {
      const status = err?.response?.status;
      if (status === 401) showPopup("Phiên đăng nhập đã hết hạn, vui lòng đăng nhập lại", "error");
      else showPopup(err?.response?.data?.message || "Tên thư mục đã tồn tại!", "warning");
    }

    closeCreateModal();
  };

  const closeCreateModal = () => {
    setShowCreateModal(false);
    setNewFolderName("");
    setNewFolderLocked(false);
    setNewFolderPassword("");
  };

  // Mở hộp đặt/đổi mật khẩu cho một thư mục con (theo tên) hoặc thư mục đang mở (name trống).
  const openLockDialog = (name) => {
    if (name) {
      setLockTarget({ path: path ? `${path}/${name}` : name, name, locked: lockedFolders.includes(name) });
    } else if (path) {
      setLockTarget({ path, name: path.split("/").pop(), locked: !!folderLock?.locked });
    }
  };

  // ================== UPLOAD ==================
  const handleUpload = async (url, formData) => {
    setUploading(true);
    setProgress(0);

    lastLoadedRef.current = 0;
    lastTimeRef.current = Date.now();

    try {
      await axios.post(url, formData, {
        headers: { "Content-Type": "multipart/form-data" },
        params: { path: path || "" },
        onUploadProgress: (e) => {
          const percent = Math.round((e.loaded * 100) / e.total);
          setProgress(percent);

          const now = Date.now();
          const dt = (now - lastTimeRef.current) / 1000;
          const dl = e.loaded - lastLoadedRef.current;

          if (dt > 0) {
            const speedMB = dl / 1024 / 1024 / dt;
            setSpeed(speedMB.toFixed(2));
            const remain = e.total - e.loaded;
            setEta((remain / (speedMB * 1024 * 1024)).toFixed(1));
          }

          lastLoadedRef.current = e.loaded;
          lastTimeRef.current = now;
        },
      });

      loadFolder(path);
      showPopup("Tải lên thành công!");
    } catch (err) {
      const msg =
        err?.response?.data?.message ||
        err?.response?.data ||
        err?.message ||
        "Lỗi không xác định";
      showPopup(`Tải lên thất bại: ${msg}`, "error");
      console.error("Upload error:", err?.response?.status, err?.response?.data);
    }

    setUploading(false);
  };

  // `files`: mảng File do hộp tải lên đưa sang.
  const uploadImages = async (files) => {
    if (!files || files.length === 0 || !validateFiles(files)) return;
    setShowUploadImageModal(false);

    const form = new FormData();
    files.forEach((f) => form.append("files", f));
    await handleUpload(`${API}/upload`, form);
  };

  const uploadFolder = async (files) => {
    const filteredFolderFiles = (files || []).filter(
      (f) => !systemFiles.includes(f.name.toLowerCase())
    );
    if (filteredFolderFiles.length === 0 || !validateFiles(filteredFolderFiles)) return;

    const form = new FormData();
    filteredFolderFiles.forEach((f) =>
      form.append("files", f, f.webkitRelativePath || f.name)
    );

    setShowUploadFolderModal(false);
    await handleUpload(`${API}/upload-folder`, form);
  };

  // ================== DOWNLOAD ==================
  const toggleDownloadSelect = (name) => {
    setSelectedDownload((prev) =>
      prev.includes(name) ? prev.filter((n) => n !== name) : [...prev, name]
    );
  };

  const toggleSelectAll = () => {
    if (selectedDownload.length === images.length) {
      setSelectedDownload([]);
    } else {
      setSelectedDownload(images.map((i) => i.name));
    }
  };

  const handleDownload = async () => {
    if (selectedDownload.length === 0) return;
    setDownloading(true);
    try {
      await downloadFiles(path, selectedDownload);
      showPopup("Tải về thành công!");
      setShowDownloadModal(false);
      setSelectedDownload([]);
    } catch {
      showPopup("Tải về thất bại!", "error");
    }
    setDownloading(false);
  };

  // ================== COMPRESS ==================
  const handleCompressAll = async () => {
    if (compressing) return;
    setConfirmData(null);
    setCompressing(true);
    try {
      const stats = await compressAllImages(5);
      const savedMB = (stats.bytesSaved / 1024 / 1024).toFixed(1);
      showPopup(
        `Đã nén ${stats.filesCompressed}/${stats.filesScanned} ảnh, tiết kiệm ${savedMB} MB`
      );
      loadFolder(path);
    } catch {
      showPopup("Nén ảnh thất bại!", "error");
    }
    setCompressing(false);
  };

  // Nén toàn bộ kho ghi đè lên ảnh gốc và không hoàn tác được, nên hỏi lại trước.
  const askCompressAll = () => {
    if (compressing) return;
    setConfirmData({
      title: "Nén toàn bộ ảnh?",
      icon: <IcoCompress size={20} />,
      warning: "Ảnh sau khi nén được ghi đè lên ảnh gốc và không khôi phục lại được.",
      message: "Mọi ảnh JPG/PNG nặng hơn 5 MB trong toàn bộ kho ảnh sẽ được nén lại cho nhẹ hơn. Việc này có thể mất vài phút.",
      confirmLabel: "Nén ảnh",
      onConfirm: handleCompressAll,
    });
  };

  const askDeleteImage = (name) => {
    const used = usage.files[name];
    setConfirmData({
      title: "Chuyển vào thùng rác?",
      icon: <IcoTrash size={20} />,
      warning: used ? `${usageText(used)} Xóa sẽ làm mất ảnh ở nơi đó.` : null,
      message: `File "${name}" sẽ được chuyển vào thùng rác. File được giữ 30 ngày và có thể khôi phục.`,
      confirmLabel: "Chuyển vào thùng rác",
      onConfirm: () => deleteImage(name),
    });
  };

  const deleteImage = async (name) => {
    try {
      await axios.delete(`${API}/delete-file`, {
        params: {
          path: path || "",
          name: name,
        },
      });

      setConfirmData(null);
      loadFolder(path);
      showPopup("Đã chuyển file vào thùng rác");
    } catch (err) {
      console.error("Delete image failed:", err);
      showPopup("Xóa file thất bại!", "error");
      setConfirmData(null);
    }
  };

  const renameImage = async (oldName, newName) => {
    try {
      await axios.post(`${API}/rename-file`, { path, oldName, newName });
      loadFolder(path);
      showPopup("Đổi tên file thành công!");
    } catch {
      showPopup("Tên file đã tồn tại!", "warning");
    }
  };

  const askDeleteFolder = (folder) => {
    const usedCount = usage.folders[folder] || 0;
    setDeleteFolderConfirm({
      folder,
      warning: usedCount > 0
        ? `Có ${usedCount} ảnh trong thư mục này đang được website hoặc app sử dụng.`
        : null,
      message: `Thư mục "${folder}" sẽ được chuyển vào thùng rác (giữ 30 ngày, có thể khôi phục). Vui lòng nhập lại mật khẩu để xác nhận.`,
    });
  };

  const deleteFolder = async (folder) => {
    const fullPath = path ? `${path}/${folder}` : folder;

    await axios.delete(`${API}/delete-folder`, {
      params: { path: fullPath },
    });

    setDeleteFolderConfirm(null);
    loadFolder(path);
    showPopup("Đã chuyển thư mục vào thùng rác");
  };

  const renameFolder = async (folderName, newName) => {
    try {
      await axios.post(`${API}/rename-folder`, {
        parent: path,
        oldName: folderName,
        newName,
      });
      loadFolder(path);
      showPopup("Đổi tên thư mục thành công!");
    } catch {
      showPopup("Tên thư mục đã tồn tại!", "warning");
    }
  };

  const logout = () => {
    localStorage.removeItem("role");
    localStorage.removeItem("userName");
    localStorage.removeItem("username");
    setToken("");
    clearFolderKeys();
    setLockedInfo(null);
    setLockTarget(null);
    setRole(null);
    setUserName("");
    setUsername("");
    setPath("");
    setSearch("");
    setLoadedPath(null);
    setNavOpen(false);
    setShowLogin(true);
  };

  if (showLogin) {
    return (
      <LoginPopup
        onLogin={(r, name, u, token) => {
          localStorage.setItem("role", r);
          localStorage.setItem("userName", name);
          localStorage.setItem("username", u);
          setToken(token);
          setRole(r);
          setUserName(name);
          setUsername(u);
          setShowLogin(false);
        }}
      />
    );
  }

  const isAdmin = role === "admin";
  const pathParts = path ? path.split("/") : [];
  const pageTitle = pathParts.length > 0 ? pathParts[pathParts.length - 1] : "Tất cả thư mục";
  const targetLabel = path || "Kho ảnh (thư mục gốc)";

  // Nội dung đang hiện có đúng là của thư mục đang mở không.
  const isLocked = !!lockedInfo && lockedInfo.path === path;
  const contentReady = loadedPath === path && !isLocked;
  const totalSize = images.reduce((sum, i) => sum + (i.size || 0), 0);
  const pageMeta = [
    `${folders.length} thư mục`,
    `${images.length} tệp`,
    totalSize > 0 ? formatSize(totalSize) : null,
  ].filter(Boolean).join(" · ");

  return (
    <div className="shell">
      <Sidebar
        role={role}
        userName={userName}
        username={username}
        path={path}
        rootFolders={rootFolders}
        lockedFolders={rootLocked}
        onGoTo={goToPath}
        onOpenTrash={() => setShowTrash(true)}
        onCompressAll={askCompressAll}
        compressing={compressing}
        onLogout={logout}
        open={navOpen}
        onClose={() => setNavOpen(false)}
      />

      <div className="shell-main">
        <ActionBar
          role={role}
          path={path}
          search={search}
          onSearch={setSearch}
          onOpenNav={() => setNavOpen(true)}
          onCreate={() => setShowCreateModal(true)}
          onUploadImage={() => setShowUploadImageModal(true)}
          onUploadFolder={() => setShowUploadFolderModal(true)}
          onDownload={() => { setSelectedDownload([]); setShowDownloadModal(true); }}
          loading={folderLoading}
          locked={!!lockedInfo && lockedInfo.path === path}
        />

        <main className="page">
          <Breadcrumb path={path} setPath={goToPath} />

          <div className="page-head">
            {path && (
              <button className="icon-btn outline" onClick={goBack} title="Quay lại thư mục trước" aria-label="Quay lại">
                <IcoArrowLeft size={18} />
              </button>
            )}
            <div className="page-title">
              <h1 title={pageTitle}>{pageTitle}</h1>
              <p>{isLocked ? "Thư mục có mật khẩu" : contentReady ? pageMeta : "Đang tải thư mục…"}</p>
            </div>

            {contentReady && path && (folderLock?.locked || folderLock?.protectedByParent) && (
              <span
                className="lock-chip"
                title={folderLock.locked
                  ? "Thư mục này có mật khẩu. Link ảnh vẫn công khai."
                  : "Thư mục cha có mật khẩu nên thư mục này cũng được bảo vệ."}
              >
                <IcoLock size={13} /> Có mật khẩu
              </span>
            )}
            {contentReady && path && folderLock?.manageable && (
              <button className="btn btn-quiet btn-sm page-lock-btn" onClick={() => openLockDialog(null)}>
                <IcoLock size={15} />
                <span>{folderLock.locked ? "Đổi mật khẩu" : "Đặt mật khẩu"}</span>
              </button>
            )}
          </div>

          {!folderLoading && folderError && (
            <div className="callout danger page-error">
              <IcoAlert size={18} />
              <span>{folderError}</span>
              <button className="btn btn-quiet btn-sm" onClick={() => loadFolder(path)}>
                Thử lại
              </button>
            </div>
          )}

          {isLocked ? (
            <LockedFolderPanel
              path={path}
              lockPath={lockedInfo.lockPath}
              ownerName={lockedInfo.ownerName}
              onUnlocked={() => loadFolder(path)}
              onBack={goBack}
            />
          ) : contentReady ? (
            <div className={folderLoading ? "is-refreshing" : ""}>
              <ExplorerView
                role={role}
                folders={folders}
                images={images}
                path={path}
                search={search}
                onOpenFolder={goIntoFolder}
                onDeleteFolder={isAdmin ? askDeleteFolder : null}
                onDeleteImage={isAdmin ? askDeleteImage : null}
                onRenameFolder={isAdmin ? renameFolder : null}
                onRenameImage={renameImage}
                onUploadClick={() => setShowUploadImageModal(true)}
                onNotify={showPopup}
                usage={usage}
                lockedFolders={lockedFolders}
                manageableFolders={manageableFolders}
                onManageLock={openLockDialog}
              />
            </div>
          ) : (
            !folderError && (
              <div className="skeleton" aria-hidden="true">
                <div className="skeleton-row">
                  {Array.from({ length: 4 }, (_, i) => <span key={i} className="sk sk-tile" />)}
                </div>
                <div className="skeleton-grid">
                  {Array.from({ length: 10 }, (_, i) => <span key={i} className="sk sk-card" />)}
                </div>
              </div>
            )
          )}

          <footer className="page-foot">
            <span>© {new Date().getFullYear()} Hùng Vương Gia Lai</span>
            <span>Powered by Phòng CNTT - leanhquan™</span>
          </footer>
        </main>
      </div>

      {uploading && (
        <UploadProgress progress={progress} speed={speed} eta={eta} />
      )}

      {confirmData && (
        <ConfirmDialog
          title={confirmData.title}
          icon={confirmData.icon}
          message={confirmData.message}
          warning={confirmData.warning}
          confirmLabel={confirmData.confirmLabel}
          onConfirm={confirmData.onConfirm}
          onCancel={() => setConfirmData(null)}
        />
      )}

      {deleteFolderConfirm && (
        <ConfirmPasswordDialog
          username={username}
          message={deleteFolderConfirm.message}
          warning={deleteFolderConfirm.warning}
          onConfirm={() => deleteFolder(deleteFolderConfirm.folder)}
          onCancel={() => setDeleteFolderConfirm(null)}
        />
      )}

      {lockTarget && (
        <LockDialog
          folder={lockTarget}
          userName={userName || username}
          onCancel={() => setLockTarget(null)}
          onDone={(message) => {
            setLockTarget(null);
            showPopup(message);
            loadFolder(path);
          }}
        />
      )}

      {showTrash && (
        <TrashModal
          onClose={() => setShowTrash(false)}
          onChanged={() => loadFolder(path)}
          showPopup={showPopup}
        />
      )}

      {notifyCenter && (
        <NotifyCenter message={notifyCenter} type={notifyType} />
      )}

      {/* TẠO THƯ MỤC */}
      {isAdmin && showCreateModal && (
        <div className="modal" role="dialog" aria-modal="true" aria-label="Tạo thư mục mới">
          <form
            className="modal-card"
            onSubmit={(e) => { e.preventDefault(); createFolder(); }}
          >
            <div className="modal-head">
              <span className="modal-ico"><IcoFolderPlus size={20} /></span>
              <div>
                <h3>Tạo thư mục mới</h3>
                <p>Trong: <b>{targetLabel}</b></p>
              </div>
              <button type="button" className="icon-btn ghost modal-x" onClick={closeCreateModal} aria-label="Đóng">
                <IcoX size={20} />
              </button>
            </div>

            <div className="modal-body">
              <label className="field">
                <span>Tên thư mục</span>
                <input
                  type="text"
                  value={newFolderName}
                  onChange={(e) => setNewFolderName(e.target.value)}
                  placeholder="Tên thư mục…"
                  autoFocus
                />
              </label>

              <label className="check-row check-single">
                <input
                  type="checkbox"
                  checked={newFolderLocked}
                  onChange={(e) => setNewFolderLocked(e.target.checked)}
                />
                <span>Đặt mật khẩu cho thư mục này</span>
              </label>
              {newFolderLocked && (
                <label className="field">
                  <span>Mật khẩu (ít nhất {MIN_PASSWORD_LENGTH} ký tự)</span>
                  <PasswordInput
                    value={newFolderPassword}
                    onChange={setNewFolderPassword}
                    placeholder="Người khác phải nhập mật khẩu này mới mở được…"
                  />
                </label>
              )}

              <div className="callout info">
                <IcoInfo size={16} />
                <span className="pre-line">{getUploadNote()}</span>
              </div>
            </div>

            <div className="modal-foot">
              <button type="button" className="btn btn-quiet" onClick={closeCreateModal}>
                Hủy
              </button>
              <button
                type="submit"
                className="btn btn-primary"
                disabled={!newFolderName.trim() || (newFolderLocked && newFolderPassword.length < MIN_PASSWORD_LENGTH)}
              >
                Tạo thư mục
              </button>
            </div>
          </form>
        </div>
      )}

      {/* TẢI FILE LÊN */}
      {showUploadImageModal && (
        <UploadModal
          mode="files"
          title="Tải ảnh, video, PDF lên"
          targetLabel={targetLabel}
          note={getUploadNote()}
          allowedExt={allowedExt}
          skipNames={systemFiles}
          onSubmit={uploadImages}
          onCancel={() => setShowUploadImageModal(false)}
        />
      )}

      {/* TẢI THƯ MỤC LÊN */}
      {isAdmin && showUploadFolderModal && (
        <UploadModal
          mode="folder"
          title="Tải thư mục lên"
          targetLabel={targetLabel}
          note={getUploadNote()}
          allowedExt={allowedExt}
          skipNames={systemFiles}
          onSubmit={uploadFolder}
          onCancel={() => setShowUploadFolderModal(false)}
        />
      )}

      {/* TẢI VỀ */}
      {showDownloadModal && (
        <div className="modal" role="dialog" aria-modal="true" aria-label="Tải file về">
          <div className="modal-card modal-wide modal-tall">
            <div className="modal-head">
              <span className="modal-ico"><IcoDownload size={20} /></span>
              <div>
                <h3>Tải file về</h3>
                <p>Chọn nhiều file sẽ được gói thành một file .zip.</p>
              </div>
              <button className="icon-btn ghost modal-x" onClick={() => setShowDownloadModal(false)} aria-label="Đóng">
                <IcoX size={20} />
              </button>
            </div>

            <div className="modal-body modal-scroll">
              <label className="check-row check-all">
                <input
                  type="checkbox"
                  checked={selectedDownload.length === images.length && images.length > 0}
                  onChange={toggleSelectAll}
                />
                <span>Chọn tất cả ({images.length} file)</span>
              </label>

              <div className="check-list">
                {images.map((item) => (
                  <label
                    key={item.name}
                    className={`check-row ${selectedDownload.includes(item.name) ? "on" : ""}`}
                  >
                    <input
                      type="checkbox"
                      checked={selectedDownload.includes(item.name)}
                      onChange={() => toggleDownloadSelect(item.name)}
                    />
                    <span className="check-name" title={item.name}>{item.name}</span>
                    {item.size > 0 && <span className="check-size">{formatSize(item.size)}</span>}
                  </label>
                ))}
                {images.length === 0 && (
                  <p className="list-empty">Không có file nào</p>
                )}
              </div>
            </div>

            <div className="modal-foot">
              <button className="btn btn-quiet" onClick={() => setShowDownloadModal(false)}>
                Hủy
              </button>
              <button
                className="btn btn-primary"
                onClick={handleDownload}
                disabled={selectedDownload.length === 0 || downloading}
              >
                <IcoDownload size={17} />
                <span>{downloading ? "Đang tải…" : `Tải về (${selectedDownload.length})`}</span>
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
